import { useState } from "react";
import { Input } from "@/frontend/components/ui/input";
import { CopyField } from "./CopyField";
import { DocsHelpLink } from "./DocsHelpLink";
import { Switch } from "@/frontend/components/ui/switch";

type Props = {
  /** Whether custom-credentials mode is on (vs. Medblocks-supplied credentials). */
  enabled: boolean;
  /** Toggles between custom and Medblocks credentials. */
  onToggle: () => void;
  /** Current custom Client ID value. */
  clientId: string;
  /** Called with the new Client ID on every keystroke. */
  onClientIdChange: (value: string) => void;
  /** Provider registration URL, linked as "Register app at {label}". */
  registrationUrl: string;
  /** Provider display label, used in the registration link text. */
  providerLabel: string;
  /** Medblocks docs URL for this provider's integration guide; renders as a (?) tooltip next to the heading. */
  docsUrl?: string;
  /** SMART redirect URI shown as a read-only copy field. Omit + `showRedirectUri={false}` to hide (e.g. backend services). */
  redirectUri?: string;
  /** JWKS URI shown as a read-only copy field. Omit + `showJwksUri={false}` to hide. */
  jwksUri?: string;
  /** Whether to render the redirect URI copy field. Defaults to true. */
  showRedirectUri?: boolean;
  /** Whether to render the JWKS URI copy field. Defaults to true. */
  showJwksUri?: boolean;
  /** Disables the toggle while a mutation is in flight. */
  disabled?: boolean;
  /** Optional server error to surface below the fields. */
  error?: string | null;
  /** Whether the server already has a stored client secret for this provider. */
  hasSecret?: boolean;
  /** New client-secret value entered by the user; empty string = no change. */
  clientSecret?: string;
  /** Called on every keystroke of the client-secret input. When provided, the
   *  client-secret field renders unconditionally; when omitted, the client
   *  secret UI is hidden entirely. Caller decides per-provider (see
   *  `CLIENT_SECRET_PROVIDERS`). */
  onClientSecretChange?: (value: string) => void;
};

/**
 * The "Use custom credentials" card shared by the patient/practitioner/backend
 * EHR detail views. Renders a toggle between Medblocks-supplied and custom OAuth
 * credentials; when custom mode is on it reveals the Client ID input (with a
 * required-field validation that appears after the field is touched) plus the
 * read-only Redirect URI and JWKS URI copy fields.
 */
export function CustomCredentialsCard({
  enabled,
  onToggle,
  clientId,
  onClientIdChange,
  registrationUrl,
  providerLabel,
  docsUrl,
  redirectUri,
  jwksUri,
  showRedirectUri = true,
  showJwksUri = true,
  disabled,
  error,
  hasSecret = false,
  clientSecret = "",
  onClientSecretChange,
}: Props) {
  const [blurred, setBlurred] = useState(false);
  const showRequiredError = enabled && blurred && !clientId.trim();

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="px-5 py-4 flex items-start gap-4">
        <Switch checked={enabled} onCheckedChange={onToggle} disabled={disabled} />
        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium leading-tight">Use custom credentials</p>
            {docsUrl && (
              <DocsHelpLink
                href={docsUrl}
                tooltip={`Read the ${providerLabel} integration guide`}
                ariaLabel={`Open ${providerLabel} custom-credentials guide`}
                iconClassName="size-3.5"
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {enabled ? "Using your own credentials." : "Using Medblocks' credentials."}{" "}
            <a
              href={registrationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Register app at {providerLabel}
            </a>
          </p>
        </div>
      </div>

      {enabled && (
        <div className="border-t px-5 py-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Client ID <span className="text-destructive">*</span>
            </span>
            <Input
              value={clientId}
              onChange={(e) => { onClientIdChange(e.target.value); setBlurred(true); }}
              onBlur={() => setBlurred(true)}
              placeholder="Enter your Client ID"
              className={`font-mono text-xs bg-background ${showRequiredError ? "border-destructive focus-visible:ring-destructive" : ""}`}
            />
            {showRequiredError && (
              <p className="text-xs text-destructive">Client ID is required</p>
            )}
          </div>
          {onClientSecretChange && (
            <div className="flex flex-col gap-1.5">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                Client Secret
                <DocsHelpLink
                  href={docsUrl ?? registrationUrl}
                  tooltip={`${providerLabel} authenticates via client_secret_basic — supply the secret from your registered app.`}
                  ariaLabel="About client secret"
                  iconClassName="size-3.5"
                />
              </span>
              <Input
                type="password"
                autoComplete="off"
                value={clientSecret}
                onChange={(e) => onClientSecretChange(e.target.value)}
                placeholder={hasSecret ? "Leave blank to keep current secret" : "Enter your client secret"}
                className="font-mono text-xs bg-background"
              />
            </div>
          )}
          {showRedirectUri && redirectUri !== undefined && <CopyField label="Redirect URI" value={redirectUri} />}
          {showJwksUri && jwksUri !== undefined && <CopyField label="JWKS URI" value={jwksUri} />}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
}
